<template>
  <div :class="['pay-block', { 'with_shadow': !noShadow }]">
    <div class="app-card-item">
      <div class="book-preview relative-position bg-grey-6">
        <q-img
          :src="book.coverImageUrl"
          class="book-preview__cover"
        />
        <div class="book-preview__shield bg-white" />
      </div>
    </div>

    <div class="pay-block__info">
      <h1 v-html="title" />
      <h3 v-html="text" />

      <div class="pay-block__info__input-button row justify-center items-center q-mb-md">
        <template v-if="isPasswordType">
          <AppInput
            v-model="password"
            outlined
            :placeholder="$t('auth.password')"
          />
          <AppButton
            :loading="isLoading"
            v-bind="btnStyle"
            padding="0 22px"
            class="border-radius-10"
            @click="getTemporaryToken"
          />
        </template>
        <template v-if="accessType === BOOK_ACCESS_TYPES.PAYWALL">
          <AppButton
            class="long-button border-radius-10"
            v-bind="btnStyle"
            @click="openPaywallLink"
          >
            <div class="text-uppercase">&nbsp;• {{ book.access.data.price }} {{ book.access.data.currency }}</div>
          </AppButton>
        </template>
        <template v-if="accessType === BOOK_ACCESS_TYPES.LINK">
          <AppInput
            v-model="email"
            outlined
            :placeholder="$t('auth.email')"
          />
          <AppButton
            class="short-button border-radius-10"
            :loading="isLoading"
            v-bind="btnStyle"
            padding="0 22px"
            @click="handleLink"
          />
        </template>
        <template v-if="accessType === BOOK_ACCESS_TYPES.SHARE">
          <AppButton
            class="long-button border-radius-10"
            v-bind="btnStyle"
            :label="btnStyle.label || $t('text.shareToUnlock')"
            padding="10px 22px"
          >
            <q-popup-proxy
              anchor="top middle"
              self="bottom middle"
              class="border-radius-10"
              content-class="share-popup border-radius-20"
              :offset="[0, 30]"
              autoclose
            >
              <div class="q-px-md q-py-lg bg-white share-inner flex no-wrap q-gutter-x-sm">
                <ShareNetwork
                  v-for="network in networks"
                  :key="network.network"
                  :network="network.network"
                  :url="sharing.url"
                  :title="sharing.title"
                  :media="sharing.image"
                  :description="sharing.description"
                  :style="{ color: network.color }"
                  class="flex items-center border-radius-10 justify-center"
                  @open="getTemporaryToken"
                >
                  <q-icon
                    :name="network.icon"
                    :style="{ color: network.color }"
                    size="18px"
                  />
                </ShareNetwork>
              </div>
            </q-popup-proxy>
          </AppButton>
        </template>
      </div>
      <div
        v-if="showLoginLink"
        :style="linkStyle"
        class="text-grey-7"
        v-html="$t('text.loginTourAccount', { href: loginLink })"
      />
    </div>
    <AppModal
      v-if="currentModal"
      model-value
      :title="modal.title"
      :content-style="modalStyle"
      body-class="col full-width"
      header-class="bg-white no-border"
      no-maximized
      show-close-btn
      @hide="currentModal = undefined"
    >
      <div class="modal-container text-center">
        <q-img
          :src="`/${modal.icon}.svg`"
          class="modal-icon"
        />
        <div class="text-31-700 q-my-md">
          {{ modal.text }}
        </div>
        <div class="text-16-400 text-grey-13">
          {{ modal.description }}
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script>
import { i18n } from 'src/boot/i18n';
import AppInput from 'components/AppInput.vue';
import AppModal from 'components/AppModal.vue';
import AppButton from 'components/AppBtn.vue';
import { setReaderAccessToken } from 'src/helpers/cookies';
import handleError from 'src/helpers/handleError';
import BOOK_ACCESS_TYPES from 'src/enums/bookAccessTypes';
import { mapStores } from 'pinia';
import { useDocumentsStore } from 'stores/documents';

const MODALS = {
  PASSWORD_ERROR: 'password-error',
  LINK_SUCCESS: 'link-success',
};

export default {
  name: 'PayBlock',
  components: {
    AppInput,
    AppButton,
    AppModal,
  },
  inject: ['axios'],
  props: {
    accessType: {
      type: String,
      default: undefined,
    },
    noShadow: {
      type: Boolean,
      default: false,
    },
  },
  modalsMap: {
    [MODALS.PASSWORD_ERROR]: {
      title: i18n.global.t('title.passwordError'),
      text: i18n.global.t('title.passwordDidntWork'),
      description: i18n.global.t('text.checkYourEntry'),
      icon: 'error',
    },
    [MODALS.LINK_SUCCESS]: {
      title: i18n.global.t('title.access'),
      text: i18n.global.t('title.accessSentToEmail'),
      description: i18n.global.t('text.checkInbox'),
      icon: 'access',
    },
  },
  emits: ['success', 'refresh-book'],
  data() {
    return {
      networks: [
        {
          network: 'facebook',
          icon: 'fab fah fa-lg fa-facebook',
          color: '#094AF1',
        },
        {
          network: 'linkedin',
          icon: 'fab fah fa-lg fa-linkedin',
          color: '#0B66C3',
        },
        {
          network: 'twitter',
          icon: 'fab fah fa-lg fa-twitter',
          color: '#10C9F1',
        },
        {
          network: 'telegram',
          icon: 'fab fah fa-lg fa-telegram-plane',
          color: '#258BC0',
        },
        {
          network: 'pinterest',
          icon: 'fab fah fa-lg fa-pinterest',
          color: '#E60023',
        },
      ],
      showLoginLink: true,
      isLoading: false,
      password: undefined,
      email: undefined,
      currentModal: undefined,
    };
  },
  computed: {
    ...mapStores(useDocumentsStore),
    book() {
      return this.documentsStore.bookPreview;
    },
    BOOK_ACCESS_TYPES: () => BOOK_ACCESS_TYPES,
    sharing() {
      return {
        url: this.book.publicUrl,
        title: this.book.title,
        description: this.book.metaFields.metaDescription,
        image: this.book.coverImageUrl,
      };
    },
    title() {
      return this.getMetaTypeProp('lockScreenTitle');
    },
    text() {
      return this.getMetaTypeProp('lockScreenDescription');
    },
    buttonText() {
      return this.getMetaTypeProp('lockScreenButtonText');
    },
    buttonIcon() {
      return this.getMetaTypeProp('lockScreenButtonIcon');
    },
    loginLink() {
      return `https://${process.env.SERVICE_DOMAIN}/my/bookshelf`;
    },
    modal() {
      return this.$options.modalsMap[this.currentModal];
    },
    modalStyle() {
      return {
        width: `${Math.min(500, this.$el?.clientWidth)}px`,
      };
    },
    linkStyle() {
      return {
        '--link-color': `${this.book.metaFields.lockScreenButtonColorBackground} !important`,
      };
    },
    btnStyle() {
      return {
        style: {
          color: `${this.book.metaFields.lockScreenButtonColorText} !important`,
          background: `${this.book.metaFields.lockScreenButtonColorBackground} !important`,
          minHeight: '50px',
          fontWeight: 400,
          '--icon-right-color': this.book.metaFields.lockScreenButtonColorIcon,
        },
        iconRight: this.getMetaTypeProp('lockScreenButtonIcon'),
        label: this.buttonText,
      };
    },
    isPasswordType() {
      return this.accessType === BOOK_ACCESS_TYPES.PASSWORD;
    },
  },
  methods: {
    getMetaTypeProp(property) {
      const type = this.accessType;
      const propertyType = [type[0].toUpperCase(), ...type.split('')
        .slice(1)].join('');
      return this.book.metaFields[`${property}${propertyType}`];
    },
    async getTemporaryToken() {
      this.isLoading = true;
      try {
        const accessData = {
          type: this.accessType,
          data: {},
        };

        if (this.isPasswordType) {
          accessData.data.password = this.password;
        }

        const { data } = await this.axios.post(`public/books/${this.book.guid}/temporary-token`, { accessData });
        setReaderAccessToken(this.book.slug, data.data.token);
        this.$emit('success');
        this.$emit('refresh-book');
      } catch {
        if (this.isPasswordType) {
          this.currentModal = MODALS.PASSWORD_ERROR;
        }
      } finally {
        this.isLoading = false;
      }
    },
    async handleLink() {
      this.isLoading = true;
      try {
        await this.axios.post(`public/books/${this.book.guid}/leads`, {
          email: this.email,
        });
        this.$emit('success');
        this.currentModal = MODALS.LINK_SUCCESS;
      } catch (error) {
        handleError(error);
      } finally {
        this.isLoading = false;
      }
    },
    openPaywallLink() {
      const url = `https://www.checkout-ds24.com/product/${this.book.access.data.defaultProductId}`;
      window.open(url, '_blank');
    },
  },
};
</script>

<style scoped lang="scss">
@import 'https://fonts.googleapis.com/css2?family=Mitr:wght@200;300;400;500;600;700&display=swap';

.pay-block {
  width: 100%;
  background: #FFFFFF;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  font-family: 'Mitr', sans-serif;

  &.with_shadow {
    padding: 100px 55px;
    -webkit-box-shadow: 0px -35px 43px 33px rgba(255, 255, 255, 1);
    -moz-box-shadow: 0px -35px 43px 33px rgba(255, 255, 255, 1);
    box-shadow: 0px -35px 43px 33px rgba(255, 255, 255, 1);

    @media (max-width: $breakpoint-sm) {
      padding: 50px 0;
    }
  }

  &__info {
    padding: 0 35px;
    width: 390px;
    margin-top: 50px;
    display: flex;
    flex-direction: column;
    align-items: center;

    h1 {
      font-size: 24px;
      font-weight: 500;
      line-height: 32px;
      letter-spacing: 0;
      text-align: center;
      margin: 0;
    }

    h3 {
      margin: 0;
      margin-top: 16px;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0;
      text-align: center;
      color: $grey;
    }

    &__input-button {
      margin-top: 33px;
      gap: 20px;

      .short-button {
        height: 100%;
      }

      .long-button {
        width: 100%;
        font-size: 21px;
        font-weight: 200;
        line-height: 27px;
        letter-spacing: 0;
        text-align: center;

        .q-btn__wrapper {
          .q-btn__content {
            p {
              margin: 0;
              margin-right: 17px;
            }
          }
        }
      }
    }
  }
}

.app-card-item {
  display: flex;
  flex-direction: column;
  height: 310px;
  width: 310px;
  border-radius: 50%;

  &:hover {
    .img-container {
      background: $primary;
    }
  }

  .img-container {
    box-shadow: 0 15px 30px -13px #1F1F1F73;
    border-radius: 50%;
  }

  &__body {
    background: $white;
  }

  &__img {
    min-height: 200px;
  }

  ::v-deep .q-img {
    max-width: 100%;
    max-height: 200px;
  }

  .logo {
    top: -1px;
    max-height: 220px;
    border-radius: 10px 10px 0 0;
  }

  &__footer {
    border-top: 1px solid $grey-5;
    background: $white;
    border-radius: 0 0 10px 10px;
  }

  .btn-marketplace {
    bottom: 16px;
    right: 16px;
  }
}

.book-preview {
  width: 264px;
  height: 264px;
  border-radius: 50%;
  margin-left: auto;
  margin-right: auto;

  &__cover {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    position: absolute;
    top: 33px;
    width: 142px;
    left: calc(50% - 71px);
    max-height: 186px;
  }

  &__shield {
    width: 212px;
    height: 18px;
    position: absolute;
    bottom: 26px;
    left: calc(50% - 106px);
    box-shadow: 0 15px 40.5px 0 #1F1F1F73;
  }
}

.modal-container {
  margin: 0 auto;
  padding: 64px 0;
  max-width: 300px;
}

.modal-icon {
  width: 150px;
}

.share-inner {
  :deep(a) {
    height: 40px;
    width: 40px;
    text-decoration: none;

    &, .q-icon {
      transition: all 0.5s ease;
    }

    &:hover {
      background: currentColor;

      .q-icon {
        color: white !important;
      }
    }
  }
}
</style>

<style lang="scss">
.share-popup {
  box-shadow: 0 0 25px 0 rgba(0, 0, 0, 0.13);
}

.link-color {
  color: var(--link-color) !important;
}
</style>
